// Pleasant nudge picker — multi-day + custom time (#69, #195, #198).
//
// Layout intentionally compact (Morgan: "don't make it busy"):
//   • Row of 7 day chips (Sun-Sat). Tap to toggle. Selected chips are
//     filled; unselected are outlined.
//   • One time input below. Defaults to 9:00. Applies to every selected
//     day.
//   • Empty selection = nudge is off. No separate "Off" row needed —
//     deselecting every day is the off state.
//
// First-time tap on a day triggers the iOS notification permission
// prompt. If permission is denied, the selection silently reverts.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getNudgeDays, getNudgeTime, setNudgeSchedule,
  checkNudgePermission, requestNudgePermission,
  type WeekDay,
} from "@/utils/weekendNudge";

const DAY_ORDER: WeekDay[] = [
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
];

const DAY_SHORT: Record<WeekDay, string> = {
  sunday: "Sun", monday: "Mon", tuesday: "Tue", wednesday: "Wed",
  thursday: "Thu", friday: "Fri", saturday: "Sat",
};

const fmtTime = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

const parseTime = (s: string): { hour: number; minute: number } | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return null;
  if (!Number.isFinite(minute) || minute < 0 || minute > 59) return null;
  return { hour, minute };
};

export const WeekendNudgePicker = () => {
  const [days, setDays] = useState<WeekDay[]>([]);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [permission, setPermission] = useState<"granted" | "denied" | "prompt" | "unknown">("unknown");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDays(getNudgeDays());
    const t = getNudgeTime();
    setHour(t.hour);
    setMinute(t.minute);
    void checkNudgePermission().then(setPermission);
  }, []);

  const persist = async (
    nextDays: WeekDay[], nextHour: number, nextMinute: number,
  ) => {
    if (busy) return;
    setBusy(true);
    try {
      // If the user is selecting their first day, ensure we have
      // notification permission first.
      if (nextDays.length > 0 && permission !== "granted") {
        const perm = await requestNudgePermission();
        setPermission(perm);
        if (perm !== "granted") {
          toast.error("Notifications not allowed", {
            description: "Turn them on in iPhone Settings → Pleasant Routes → Notifications.",
          });
          return; // don't persist a state we can't actually deliver
        }
      }
      await setNudgeSchedule(nextDays, nextHour, nextMinute);
      setDays(nextDays);
      setHour(nextHour);
      setMinute(nextMinute);
      if (nextDays.length === 0) {
        toast.success("Pleasant nudge turned off.");
      } else {
        toast.success("Pleasant nudge updated.", {
          description: `${nextDays.length} day${nextDays.length === 1 ? "" : "s"} · ${fmtTime(nextHour, nextMinute)}`,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleDay = async (d: WeekDay) => {
    const isOn = days.includes(d);
    const next = isOn ? days.filter((x) => x !== d) : [...days, d];
    // Keep canonical order so we don't surprise the user with shuffles.
    next.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
    await persist(next, hour, minute);
  };

  const onTimeChange = async (value: string) => {
    const parsed = parseTime(value);
    if (!parsed) return;
    // No iOS reschedule when the nudge is off — just remember the
    // time locally for when the user turns it on.
    if (days.length === 0) {
      setHour(parsed.hour);
      setMinute(parsed.minute);
      return;
    }
    await persist(days, parsed.hour, parsed.minute);
  };

  const isOff = days.length === 0;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        A weekly reminder on the days you choose, at the time you choose.
        Pick a day to turn it on; tap it again to turn it off.
      </p>

      {/* Day chips — single compact row, wraps on narrow screens. */}
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Days">
        {DAY_ORDER.map((d) => {
          const selected = days.includes(d);
          return (
            <button
              key={d}
              type="button"
              onClick={() => void toggleDay(d)}
              disabled={busy}
              aria-pressed={selected}
              className={`flex-1 min-w-[40px] h-10 rounded-xl border text-xs font-medium transition-smooth disabled:opacity-60 ${
                selected
                  ? "border-primary bg-primary text-primary-foreground shadow-sm"
                  : "border-border bg-card text-foreground hover:bg-muted/40"
              }`}
            >
              {DAY_SHORT[d]}
            </button>
          );
        })}
      </div>

      {/* Time input — single row, label + native time picker. */}
      <label className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
        <span className="text-sm text-foreground">Time</span>
        <input
          type="time"
          value={fmtTime(hour, minute)}
          onChange={(e) => void onTimeChange(e.target.value)}
          disabled={busy}
          className="bg-transparent text-sm font-medium text-foreground focus:outline-none disabled:opacity-60"
        />
      </label>

      {/* Status hint — single line that summarizes the current state.
       *  Quieter than a banner so the layout stays calm. */}
      <p className="text-[11px] text-muted-foreground">
        {isOff
          ? "Pleasant nudge is off."
          : `${days.length} day${days.length === 1 ? "" : "s"} at ${fmtTime(hour, minute)}.`}
      </p>

      {permission === "denied" && !isOff && (
        <p className="text-[11px] text-amber-700 dark:text-amber-300">
          Notifications are blocked. Allow them in iPhone Settings →
          Pleasant Routes → Notifications to receive the nudge.
        </p>
      )}
    </div>
  );
};
